import { useContext, useEffect } from "react"
import BaseInputField from "../../../components/elements/BaseInputField"
import { CostCenterType, Employee } from "../types/Types"
import BaseSelection from "../../../components/elements/BaseSelection"
import BaseInputArea from "../../../components/elements/BaseInputArea"
import { ReactComponent as CLOSE } from "../../../assets/CloseICON.svg"
import { Form } from "antd"
import dayjs from "dayjs"
import { SelfUserContext } from "../../../components/contexts/SelfUserContext"
import BasePopover from "../../../components/elements/BasePopover"
import BaseSection from "../../../components/elements/BaseSection"
import BaseDivider from "../../../components/elements/BaseDivider"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"

type EmployeeDetailPartType = {
    updatedEmployee: Employee | undefined
    setupdatedEmployee: (e: any) => void // TODO any
    resetDataToOriginal: () => void
    displayedEmployee: Employee
    sendInvite: () => void
    openSetPassword: () => void
    kcGroups: any // TODO any
    costcenter: any[]
    handleUpdateEmployee: () => void
}

export default function EmployeeDetailPart(props: EmployeeDetailPartType) {
    const ability = useAbility()
    const [form] = Form.useForm()
    useEffect(() => {
        form.setFieldsValue({
            first_name: props.displayedEmployee.first_name,
            last_name: props.displayedEmployee.last_name,
            birthday: dayjs(props.displayedEmployee.geb).format("YYYY-MM-DD"),
            position: props.displayedEmployee.position,
            contract_start: props.displayedEmployee.contractStart,
            contract_end: props.displayedEmployee.contractEnd,
            email: props.displayedEmployee?.email,
            costcenter: (props.displayedEmployee?.kostenstelle as CostCenterType[]).map((item: CostCenterType) => item.name),
            notes: props.displayedEmployee?.notes,
            vacationContingent: props.displayedEmployee?.vacationContingentPerYear,
            street: props.displayedEmployee?.address?.street,
            nr: props.displayedEmployee?.address?.nr,
            plz: props.displayedEmployee?.address?.plz,
            city: props.displayedEmployee?.address?.city,
        })
    }, [form, props.displayedEmployee])

    const userSelf = useContext(SelfUserContext)
    // früher `position === "Admin"`: das Recht haben laut Gruppenkatalog nur Admin und WicodeAdmin
    const canManagePermissions = ability.can(P.mitarbeiter_manage_permissions.action, P.mitarbeiter_manage_permissions.subject)
    const disableIfUserIsSelfAndNotAdmin = userSelf?.user?._id === props.displayedEmployee?._id && !canManagePermissions

    return (
        <BaseSection responsive="">
            <Form
                form={form}
                className="w-full h-full overflow-x-hidden overflow-y-hidden lg:overflow-y-scroll"
                onFinish={() => {
                    props.handleUpdateEmployee()
                }}>
                <div className="flex flex-col sm:flex-row justify-between items-center sm:items-end gap-2">
                    <h3 className="font-bold text-2xl pt-2 text-center sm:text-left border-b pb-2 mb-4 sm:mb-0 sm:border-none sm:pb-0">
                        Persönliche Informationen
                    </h3>
                    {/* ohne kcId gibt es kein Konto, das die /kc-Routen ansprechen könnten */}
                    {!!props.displayedEmployee.kcId && (userSelf?.user._id !== props.displayedEmployee._id || canManagePermissions) && (
                        <div className="flex flex-wrap justify-center sm:justify-end gap-1 min-w-0">
                            <Authorized allOf={[P.mitarbeiter_read_write_ou]}>
                                <BasePopover
                                    label={"Einladung senden?"}
                                    text={"Der Mitarbeiter erhält per E-Mail einen Link (14 Tage gültig), mit dem er sein Passwort festlegt. Ein bestehendes Passwort bleibt bis dahin gültig."}
                                    okButtonClick={props.sendInvite}>
                                    <div className="btn btn-ghost btn-sm">Einladung senden</div>
                                </BasePopover>
                            </Authorized>
                            <Authorized allOf={[P.mitarbeiter_manage_permissions]}>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={props.openSetPassword}>
                                    Passwort setzen
                                </button>
                            </Authorized>
                        </div>
                    )}
                </div>
                <div className="flex flex-col lg:overflow-y-scroll w-full overflow-x-hidden items-center">
                    <div className="w-full border-b my-3" />
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full" name="first_name" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                label={"Vorname"}
                                type={"text"}
                                onChange={(newFirstName: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        first_name: newFirstName,
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-2 w-full" name="last_name" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                label={"Nachname"}
                                type={"text"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        last_name: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex flex-col md:flex-row md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full" name="birthday" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Geburtstag"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"date"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        geb: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-2 w-full" name="position" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseSelection
                                disabled={!ability.can(P.mitarbeiter_manage_permissions.action, P.mitarbeiter_manage_permissions.subject) || disableIfUserIsSelfAndNotAdmin}
                                label={"Position"}
                                setId={(newItem: string) => {
                                    form.setFieldsValue({ position: newItem })
                                    props.setupdatedEmployee((prev: any) => ({
                                        ...prev,
                                        kcId: newItem,
                                    }))
                                }}
                                placeholder={"Position auswählen"}
                                data={props.kcGroups.rbac.map((item: { id: string; name: string }) => item.name)}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex flex-col md:flex-row md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full" name="contract_start" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Vertragsbeginn"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"date"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        contractStart: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-2 w-full" name="contract_end" rules={[{ required: false, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                label={"Vertragsende (optional)"}
                                type={"date"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        contractEnd: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full" name="email" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"E-Mail"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"email"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        email: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            className="mb-2 w-full"
                            name="vacationContingent"
                            rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Urlaubsanspruch"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"number"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        vacationContingentPerYear: newItem,
                                    }))
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
                        <div className="flex flex-row lg:justify-evenly gap-4 md:py-0 w-full items-center">
                            <div className="form-control">
                                <label className="label cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={props.updatedEmployee?.vehicle ?? props.displayedEmployee.vehicle}
                                        onChange={() => {
                                            props.setupdatedEmployee((prevState: any) => ({
                                                ...prevState,
                                                vehicle: !props.updatedEmployee?.vehicle,
                                            }))
                                        }}
                                        disabled={disableIfUserIsSelfAndNotAdmin}
                                        className="checkbox"
                                    />
                                    <span className="label-text pl-4">Dienstwagen</span>
                                </label>
                            </div>
                            <div className="form-control">
                                <label className="label cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={props.updatedEmployee?.trailperiod ?? props.displayedEmployee?.trailperiod}
                                        className="checkbox"
                                        disabled={disableIfUserIsSelfAndNotAdmin}
                                        onChange={() => {
                                            props.setupdatedEmployee((prevState: any) => ({
                                                ...prevState,
                                                trailperiod: !props.updatedEmployee?.trailperiod,
                                            }))
                                        }}
                                    />
                                    <span className="label-text pl-4">Probezeit</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <Form.Item className="mb-2 w-full" name="costcenter" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                        <BaseSelection
                            mode="multiple"
                            disabled={disableIfUserIsSelfAndNotAdmin}
                            label={"Kostenstelle"}
                            setId={(newItemArray: any) => {
                                form.setFieldsValue({ costcenter: newItemArray })
                                let ids = props.costcenter
                                    ?.filter((item: { _id: string; name: string }) => newItemArray.includes(item.name))
                                    .map((item) => item._id)
                                props.setupdatedEmployee((prevState: any) => ({
                                    ...prevState,
                                    kostenstelle: ids,
                                }))
                            }}
                            placeholder={"Kostenstelle auswählen"}
                            data={props.costcenter?.map((item: { _id?: string; name: string }) => item.name)}
                        />
                    </Form.Item>
                    <Form.Item className="mb-2 w-full" name="notes" rules={[{ required: false, message: "Muss einen Wert enthalten" }]}>
                        <BaseInputArea
                            disabled={disableIfUserIsSelfAndNotAdmin}
                            label={"Bemerkung"}
                            onChange={(newItem: string) => {
                                props.setupdatedEmployee((prevState: any) => ({
                                    ...prevState,
                                    notes: newItem,
                                }))
                            }}
                        />
                    </Form.Item>
                    <BaseDivider classProps="mb-2 mt-4" />
                    <p className="text-sm border-b pb-1.5 text-start w-full mb-2">Sammelpunkt / Abfahrtsadresse</p>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full md:w-2/3" name="street" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Straße"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"text"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        address: {
                                            ...prevState?.address,
                                            street: newItem,
                                        },
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-2 w-full md:w-1/3" name="nr" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Hausnummer"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"text"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        address: {
                                            ...prevState?.address,
                                            nr: newItem,
                                        },
                                    }))
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
                        <Form.Item className="mb-2 w-full md:w-1/3" name="plz" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"PLZ"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"text"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        address: {
                                            ...prevState?.address,
                                            plz: newItem,
                                        },
                                    }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-2 w-full md:w-2/3" name="city" rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                            <BaseInputField
                                label={"Stadt"}
                                disabled={disableIfUserIsSelfAndNotAdmin}
                                type={"text"}
                                onChange={(newItem: string) => {
                                    props.setupdatedEmployee((prevState: any) => ({
                                        ...prevState,
                                        address: {
                                            ...prevState?.address,
                                            city: newItem,
                                        },
                                    }))
                                }}
                            />
                        </Form.Item>
                    </div>
                    <BaseDivider classProps="mb-2 mt-4" />
                    <Authorized allOf={[P.mitarbeiter_read_write]}>
                        {props.updatedEmployee !== undefined && (
                            <div className="w-[98%] flex items-center h-fit justify-between">
                                <button type="reset" className="btn btn-sm btn-error btn-outline" onClick={props.resetDataToOriginal}>
                                    <CLOSE className="w-6 h-6" />
                                </button>
                                <button className="btn btn-sm btn-primary" type="submit" onClick={props.handleUpdateEmployee}>
                                    Speichern
                                </button>
                            </div>
                        )}
                    </Authorized>
                </div>
            </Form>
        </BaseSection>
    )
}
